<template>
  <div class="workforce-cost-addon">
    <div class="addon-head">
      <div>
        <span>HRM_WORKFORCE_COST_DASHBOARD</span>
        <h2>人效成本驾驶舱</h2>
        <p>聚合薪资、企业社保公积金、部门人数和出勤样本，形成成本结构、人均成本和人效风险看板。</p>
      </div>
      <el-button type="primary" icon="el-icon-refresh" :loading="loading" @click="loadData">刷新</el-button>
    </div>

    <el-row :gutter="16" class="metric-row">
      <el-col v-for="item in metrics" :key="item.label" :xs="24" :sm="12" :lg="6">
        <div class="metric-card" :class="item.tone">
          <span>{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
          <em>{{ item.meta }}</em>
        </div>
      </el-col>
    </el-row>

    <el-row :gutter="16">
      <el-col :xs="24" :lg="14">
        <el-card shadow="never">
          <div slot="header">部门成本排行</div>
          <el-table :data="overview.deptCostRanking" size="small" v-loading="loading">
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="staff_count" label="人数" width="70" />
            <el-table-column prop="total_cost" label="总成本" width="100" />
            <el-table-column prop="cost_per_staff" label="人均成本" width="100" />
            <el-table-column prop="attendance_score" label="出勤分" width="90" />
            <el-table-column prop="efficiency_score" label="人效分" width="90" />
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="10">
        <el-card shadow="never">
          <div slot="header">成本结构</div>
          <div class="status-list">
            <div v-for="item in overview.costStructure" :key="item.cost_name" class="status-item">
              <span>{{ item.cost_name }}</span>
              <strong>{{ item.cost_value }}</strong>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="lower-row">
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">高效部门</div>
          <el-table :data="overview.deptEfficiencyRanking" size="small" v-loading="loading" empty-text="暂无高效部门">
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="efficiency_score" label="人效分" width="90" />
            <el-table-column prop="cost_per_staff" label="人均成本" width="100" />
            <el-table-column prop="deduct_ratio" label="扣减比%" width="90" />
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">成本风险部门</div>
          <el-table :data="overview.costRiskDeptList" size="small" v-loading="loading" empty-text="暂无成本风险">
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="cost_per_staff" label="人均成本" width="100" />
            <el-table-column prop="avg_company_cost_per_staff" label="公司均值" width="100" />
            <el-table-column prop="attendance_score" label="出勤分" width="90" />
          </el-table>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="lower-row">
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">成本信号分布</div>
          <div class="status-list">
            <div v-for="item in overview.costSignalDistribution" :key="item.signal_name" class="status-item">
              <span>{{ item.signal_name }}</span>
              <strong>{{ item.signal_count }}</strong>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">月度成本趋势</div>
          <div class="trend-list">
            <div v-for="item in overview.monthlyCostTrend" :key="item.month_label" class="trend-item">
              <span>{{ item.month_label }}</span>
              <strong>{{ item.salary_total }}</strong>
              <em>人数 {{ item.salary_staff_count }} / 人均 {{ item.avg_salary }} / 扣减 {{ item.deduct_total }}</em>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { getWorkforceCostDashboardOverview } from '../api/workforceCostDashboard'

export default {
  name: 'WorkforceCostDashboardOverview',
  data () {
    return {
      loading: false,
      overview: {
        activeStaffCount: 0,
        salaryTotal: 0,
        socialCompanyCost: 0,
        avgCostPerStaff: 0,
        attendanceCostRatio: 0,
        deptCostRanking: [],
        deptEfficiencyRanking: [],
        costRiskDeptList: [],
        costStructure: [],
        monthlyCostTrend: [],
        costSignalDistribution: []
      }
    }
  },
  computed: {
    metrics () {
      return [
        { label: '在职人数', value: this.overview.activeStaffCount, meta: '成本分摊基数' },
        { label: '薪资总额', value: this.overview.salaryTotal, meta: '历史薪资样本', tone: 'blue' },
        { label: '企业社保公积金', value: this.overview.socialCompanyCost, meta: '公司承担部分', tone: 'orange' },
        { label: '人均成本', value: this.overview.avgCostPerStaff || 0, meta: `出勤效率 ${this.overview.attendanceCostRatio || 0}%`, tone: 'red' }
      ]
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    loadData () {
      this.loading = true
      getWorkforceCostDashboardOverview().then(response => {
        if (response.code === 200) {
          this.overview = Object.assign({}, this.overview, response.data || {})
        } else {
          this.$message.warning(response.message || '人效成本驾驶舱加载失败')
        }
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style lang="less" scoped>
.workforce-cost-addon {
  padding: 20px;
  background: #f5f7fb;
  min-height: 100%;
}

.addon-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
  padding: 22px 24px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.addon-head span {
  display: block;
  margin-bottom: 8px;
  color: #075985;
  font-size: 12px;
  font-weight: 700;
}

.addon-head h2 {
  margin: 0 0 8px;
  color: #17233d;
  font-size: 24px;
}

.addon-head p {
  margin: 0;
  color: #5f6f89;
}

.metric-row {
  margin-bottom: 16px;
}

.metric-card {
  min-height: 118px;
  padding: 18px;
  margin-bottom: 16px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.metric-card span,
.metric-card em {
  display: block;
  color: #6b7a90;
  font-style: normal;
}

.metric-card strong {
  display: block;
  margin: 10px 0 8px;
  color: #17233d;
  font-size: 30px;
  line-height: 1;
}

.metric-card.red strong {
  color: #d93026;
}

.metric-card.orange strong {
  color: #d9822b;
}

.metric-card.blue strong {
  color: #2f6fec;
}

.lower-row {
  margin-top: 16px;
}

.status-list,
.trend-list {
  display: grid;
  gap: 10px;
}

.trend-list {
  max-height: 360px;
  overflow: auto;
}

.status-item,
.trend-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #edf1f7;
  border-radius: 6px;
}

.trend-item {
  align-items: flex-start;
  flex-direction: column;
}

.status-item span,
.trend-item span,
.trend-item em {
  color: #5f6f89;
  font-style: normal;
}

.status-item strong,
.trend-item strong {
  color: #17233d;
  font-size: 16px;
}
</style>
